/**
 * PlatenAdvancedSection (Phase 86, UI-SPEC S6, D-12): "Advanced: platen per
 * size" for ONE ruled product. Each size shows its effective platen and
 * platen variant and where that came from (the org's rule, this product's
 * override, or nothing). An admin can move one size to another platen
 * variant for this product only, and put it back.
 *
 * Prop-driven: the parent (`DesignsTier`) passes the product's sizes, the
 * org's rules and this product's overrides from the one page-level platen
 * read. The source of every row is the shared
 * `resolveEffectivePlatenAssignment` (the same resolver the grouping uses),
 * so this table and the platen variant groups can never disagree.
 *
 * Each row has ONE Select of every platen variant ("14x16 Small", the shared
 * picker's `combined` mode), and a pick saves that size at once
 * (`productSync.setPlatenSizeOverride`, no Save button). One pick is one
 * complete pair: a separate platen and variant Select would save a
 * half-changed pair on the way ("10x12 Large" when moving from 14x16 Large to
 * 10x12 Small), making the size jump groups twice. "Reset to rule" drops the
 * override (`resetPlatenSizeOverride`). The row shows its new value
 * optimistically, disables while saving (with screen-reader "Saving" text),
 * and reverts with a persistent toast on failure. A success invalidates
 * `productSync.getPlatenSizeContext`, so the size moves to its new platen
 * variant group at once and shows as missing if that group has no file for
 * it.
 *
 * Nothing here writes Shopify metafields: the size's platen info is written
 * with its next file upload, from the same assignment as the file (D-17).
 *
 * Open state is component state only (closed on every mount, never
 * persisted). `admin` only hides the controls; the server enforces the role.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  buildSizeRuns,
  resolveEffectivePlatenAssignment,
  type PlatenAssignmentSource,
  type PlatenSizeAssignment,
  type PlatenSizeOverrides,
  type PlatenSizeRulesMap,
} from '@threadheads/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  PlatenAssignmentSelects,
  type PlatenAssignmentDraft,
} from '@/components/shared/PlatenAssignmentSelects';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { platenGroupLabel } from '@/lib/platen-size-range';

export interface PlatenAdvancedSectionProps {
  /** The Shopify product gid the overrides are keyed by. */
  productId: string;
  /** The org product type code the rules are keyed by (for example `TEE`). */
  productTypeCode: string;
  /** Every size code on the product. */
  sizes: readonly string[];
  /** The org's Platen Size rules (all product types). */
  rules: PlatenSizeRulesMap;
  /** This product's overrides, keyed by uppercase size code. */
  overrides: PlatenSizeOverrides | null | undefined;
  admin: boolean;
  /**
   * BLU-1238 (R3-2): the product keeps printing its current files, so an
   * override waits until it moves to platen files. Default false.
   */
  keepsCurrentFiles?: boolean;
}

/**
 * What this row is doing locally, ahead of the server read catching up:
 * - `saving` / `resetting`: a write in flight
 * - `set` / `reset`: the write succeeded; shown until the refetched props agree
 */
type SettledRow = { kind: 'set'; assignment: PlatenSizeAssignment } | { kind: 'reset' };
/** In-flight rows keep the settled row they started from: it is what a
 *  resetting row shows, and what a failed write reverts to. */
type LocalRow =
  | SettledRow
  | { kind: 'saving'; draft: PlatenSizeAssignment; previous: SettledRow | undefined }
  | { kind: 'resetting'; previous: SettledRow | undefined };

type Source = PlatenAssignmentSource | 'none';

const EMPTY_DRAFT: PlatenAssignmentDraft = { platen: null, platenVariant: null };

const sameAssignment = (a: PlatenSizeAssignment | null | undefined, b: PlatenSizeAssignment | null | undefined) =>
  !!a && !!b && a.platen === b.platen && a.platenVariant === b.platenVariant;

const errorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error));

export function PlatenAdvancedSection({
  productId,
  productTypeCode,
  sizes,
  rules,
  overrides,
  admin,
  keepsCurrentFiles = false,
}: PlatenAdvancedSectionProps): JSX.Element {
  const { t } = useTranslation('productSync');
  const utils = trpc.useUtils();
  const setOverride = trpc.productSync.setPlatenSizeOverride.useMutation();
  const resetOverride = trpc.productSync.resetPlatenSizeOverride.useMutation();

  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState<Record<string, LocalRow>>({});

  // The shared size ladder (the same order the platen groups use).
  const ordered = useMemo(() => buildSizeRuns(sizes).flat(), [sizes]);

  const resolve = (sizeCode: string, withOverrides: boolean) =>
    resolveEffectivePlatenAssignment({
      rules,
      overrides: withOverrides ? overrides : null,
      productTypeCode,
      sizeCode,
    });

  // Drop a finished write once the refetched props say the same thing, so the
  // server read is the only truth again.
  useEffect(() => {
    setLocal((current) => {
      let changed = false;
      const next: Record<string, LocalRow> = {};
      for (const [size, row] of Object.entries(current)) {
        const server = resolveEffectivePlatenAssignment({ rules, overrides, productTypeCode, sizeCode: size });
        const caughtUp =
          (row.kind === 'set' && server?.source === 'override' && sameAssignment(server.assignment, row.assignment)) ||
          (row.kind === 'reset' && server?.source !== 'override');
        if (caughtUp) changed = true;
        else next[size] = row;
      }
      return changed ? next : current;
    });
  }, [rules, overrides, productTypeCode]);

  const setRow = (size: string, row: LocalRow | null) =>
    setLocal((current) => {
      const next = { ...current };
      if (row) next[size] = row;
      else delete next[size];
      return next;
    });

  const errorToast = (size: string, error: unknown) =>
    toast.error(t('platen.advanced.error', { size, message: errorMessage(error) }), {
      id: `platen-advanced-error:${productId}:${size}`,
      duration: Infinity,
    });

  /** The settled row a new write starts from (in-flight rows are disabled). */
  const settled = (size: string): SettledRow | undefined => {
    const row = local[size];
    return row && (row.kind === 'saving' || row.kind === 'resetting') ? row.previous : row;
  };

  const save = async (size: string, assignment: PlatenSizeAssignment): Promise<void> => {
    const previous = settled(size);
    setRow(size, { kind: 'saving', draft: assignment, previous });
    try {
      await setOverride.mutateAsync({
        shopifyProductId: productId,
        sizeCode: size,
        platen: assignment.platen,
        platenVariant: assignment.platenVariant,
      });
      toast.dismiss(`platen-advanced-error:${productId}:${size}`);
      setRow(size, { kind: 'set', assignment });
      void utils.productSync.getPlatenSizeContext.invalidate();
    } catch (error) {
      // Revert: the Select goes back to what it showed before this change.
      setRow(size, previous ?? null);
      errorToast(size, error);
    }
  };

  const reset = async (size: string): Promise<void> => {
    const previous = settled(size);
    setRow(size, { kind: 'resetting', previous });
    try {
      await resetOverride.mutateAsync({ shopifyProductId: productId, sizeCode: size });
      toast.dismiss(`platen-advanced-error:${productId}:${size}`);
      setRow(size, { kind: 'reset' });
      void utils.productSync.getPlatenSizeContext.invalidate();
    } catch (error) {
      setRow(size, previous ?? null);
      errorToast(size, error);
    }
  };

  // The combined Select only ever reports a complete pair.
  const handleChange = (size: string, next: PlatenAssignmentDraft): void => {
    if (next.platen && next.platenVariant) {
      void save(size, { platen: next.platen, platenVariant: next.platenVariant });
    }
  };

  /** The value and source a settled row (or the server read) shows. */
  const settledView = (size: string, row: SettledRow | undefined): { value: PlatenAssignmentDraft; source: Source } => {
    switch (row?.kind) {
      case 'set':
        return { value: row.assignment, source: 'override' };
      case 'reset': {
        const rule = resolve(size, false);
        return { value: rule?.assignment ?? EMPTY_DRAFT, source: rule?.source ?? 'none' };
      }
      default: {
        const server = resolve(size, true);
        return { value: server?.assignment ?? EMPTY_DRAFT, source: server?.source ?? 'none' };
      }
    }
  };

  /** The value and source a row shows right now. */
  const viewOf = (size: string): { value: PlatenAssignmentDraft; source: Source; busy: boolean } => {
    const row = local[size];
    if (row?.kind === 'saving') {
      return { value: row.draft, source: settledView(size, row.previous).source, busy: true };
    }
    if (row?.kind === 'resetting') return { ...settledView(size, row.previous), busy: true };
    return { ...settledView(size, row), busy: false };
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-xs">
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 transition-transform duration-200 motion-reduce:transition-none',
              !open && '-rotate-90',
            )}
            aria-hidden
          />
          {t('platen.advanced.trigger')}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down motion-reduce:animate-none">
        <div className="space-y-2 pt-2">
          {/* "Change one here" only to someone who can: a non-admin sees
              the same table read-only. */}
          <p className="text-[11px] leading-4 text-muted-foreground">
            {admin ? t('platen.advanced.helper') : t('platen.advanced.helperReadOnly')}
          </p>
          {keepsCurrentFiles && (
            <p className="text-[11px] leading-4 text-muted-foreground" role="note">
              {t('platen.advanced.keptNote')}
            </p>
          )}
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left">
                  <th className="w-20 px-3 py-2 text-xs font-semibold">{t('platen.advanced.headerSize')}</th>
                  <th className="w-44 px-3 py-2 text-xs font-semibold">{t('platen.advanced.headerVariant')}</th>
                  <th className="px-3 py-2 text-xs font-semibold">{t('platen.advanced.headerSource')}</th>
                  {admin && (
                    <th className="w-28 px-3 py-2">
                      <span className="sr-only">{t('platen.advanced.reset')}</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {ordered.map((size) => {
                  const { value, source, busy } = viewOf(size);
                  return (
                    <tr key={size} className="border-b align-middle last:border-b-0" aria-busy={busy || undefined}>
                      <td className="px-3 py-2 font-mono text-xs font-semibold">{size}</td>
                      {admin ? (
                        <PlatenAssignmentSelects
                          value={value}
                          onChange={(next) => handleChange(size, next)}
                          sizeLabel={size}
                          productTypeLabel={productTypeCode}
                          size="compact"
                          layout="cells"
                          mode="combined"
                          disabled={busy}
                        />
                      ) : (
                        <td className="px-3 py-2 text-xs tabular-nums">
                          {value.platen && value.platenVariant
                            ? platenGroupLabel(t, { platen: value.platen, platenVariant: value.platenVariant })
                            : '-'}
                        </td>
                      )}
                      <td className="px-3 py-2">
                        {busy ? (
                          <span className="inline-flex items-center">
                            <Loader2
                              className="h-3.5 w-3.5 animate-spin text-muted-foreground motion-reduce:animate-none"
                              aria-hidden
                            />
                            <span className="sr-only">{t('platen.advanced.saving')}</span>
                          </span>
                        ) : (
                          <SourceChip source={source} />
                        )}
                      </td>
                      {admin && (
                        <td className="px-3 py-2 text-right">
                          {source === 'override' && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 text-xs"
                              disabled={busy}
                              onClick={() => void reset(size)}
                            >
                              {t('platen.advanced.reset')}
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function SourceChip({ source }: { source: Source }): JSX.Element {
  const { t } = useTranslation('productSync');
  if (source === 'override') {
    return (
      <Badge variant="outline" className="border-transparent font-semibold text-foreground ring-1 ring-border">
        {t('platen.advanced.sourceOverride')}
      </Badge>
    );
  }
  if (source === 'rule') {
    return <Badge variant="muted">{t('platen.advanced.sourceRule')}</Badge>;
  }
  return <span className="text-xs text-muted-foreground">{t('platen.advanced.sourceNone')}</span>;
}
